import type { Job } from "@domain/job";
import type { IJobs } from "@infra/interfaces/jobs/IJobs";

export interface JobsDetailUseCaseDeps {
	jobsApi: IJobs;
}

export interface CropOptions {
	lastCrops: string[];
	nextCrops: string[];
}

/**
 * Read path for a single job's detail page + edit modal. Wraps load-by-id,
 * update, and the parallel fetch of crop autocomplete options.
 */
export class JobsDetailUseCase {
	constructor(private readonly deps: JobsDetailUseCaseDeps) {}

	async loadById(id: string, useCache: boolean = true): Promise<Job | undefined> {
		return this.deps.jobsApi.getById(id, useCache);
	}

	async update(job: Partial<Job> & { id?: string }): Promise<Job> {
		return this.deps.jobsApi.updateOne(job);
	}

	/**
	 * Fetch both crop option lists in parallel. Editing the detail form opens
	 * an autocomplete for `lastCrop` and `nextCrop`; we want both populated
	 * before the user clicks into either.
	 */
	async loadCropOptions(): Promise<CropOptions> {
		const [lastCrops, nextCrops] = await Promise.all([
			this.deps.jobsApi.getLastCropOptions(),
			this.deps.jobsApi.getNextCropOptions(),
		]);
		return { lastCrops, nextCrops };
	}
}
